import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertComponent,
  BreadcrumbComponent,
  Button,
  MUITable,
  PaginationComponent,
  SearchInputComponent,
} from "../../../../common/components";
import {
  getOrdersListHeaders,
  ordersBreadcrumbLink,
} from "../order-management-helper";
import { Badge, useMediaQuery } from "@mui/material";
import { useSetLoading } from "../../../../common/hooks/appearance";
import icons from "../../../../assets/icons/icons";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";
import _, { capitalize } from "lodash";
import { getTenantsList, getTenets } from "../../../../api/tenets";
import { SortOption } from "../../../tenant/tenant-list/tenant-list-helper";
import OrderDetail from "./order-details/order-detail";
import CreateOrder from "./create-order/create-order";
import { routes } from "../../../../constants/routes";
import OrderFilters from "./order-filters/order-filters";
import {
  generateTempOrderId,
  getOrders,
} from "../../../../api/order-management";
import { formatDate } from "../../../../utils/utils";
import CompanyFilter from "./order-filters/company-filter/company-filter";
import "./orders.scss";
import InvoiceDetails from "./order-details/view-invoice-details";
import { useNavigate } from "react-router-dom";

const Orders: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const { canWrite } = useResourcePermissions(MODULES.ORDER_MANAGEMENT);
  const [filters, setFilters] = useState<any>({});
  const [companyFilter, setCompanyFilter] = useState<any>({});
  const [companyNames, setCompanyNames] = useState<any[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortBy: null,
    sortDirection: null,
  });
  const [data, setData] = useState<any[]>([]);
  const [crumbleMappingOption, setCrumbMappingOption] =
    useState<any>(ordersBreadcrumbLink);
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [orderId, setOrderId] = useState<string>("");


  const headers = getOrdersListHeaders();
  const navigate = useNavigate();

  const handleSearch = _.debounce((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  }, 500);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        offset: pageSize * (currentPage - 1),
        limit: pageSize,
        searchText,
        ...filters,
        ...companyFilter,
      };

      if (sortOption.sortBy) {
        params.sortBy = sortOption.sortBy;
        params.sortDirection = sortOption.sortDirection;
      }

      const response = await getOrders(params);
      setData(response?.data?.data?.orders ?? []);
      setTotalRecords(response.data.data.totalRecords);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      setAlert({
        isOpen: true,
        message: "Failed to fetch orders. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen && !isViewOpen) {
      fetchOrders();
    }
  }, [
    isOpen,
    isViewOpen,
    currentPage,
    pageSize,
    searchText,
    filters,
    companyFilter,
    sortOption,
  ]);

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      companyName: "company_name",
      customer: "first_name",
      invoiceCount: "invoice_count",
      orderId: "order_number",
      orderStatus: "order_status",
      orderDate: "created_at",
      paymentStatus: "payment_status",
      planType: "plan_type",
      totalAmount: "total_amount",
      planName: "plan_name",
    };

    return mapping[columnName] || columnName;
  };

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const getTenentList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getTenantsList({ dropDown: true });
      const items = response?.data?.data?.data?.data || [];
      const uniqueCompanies: { id: number; company_name: string, isOnboardingCompleted: boolean }[] = [];

      for (const item of items) {
        if (item?.company_name) {
          uniqueCompanies.push({
            id: item.id,
            company_name: item.company_name,
            isOnboardingCompleted: item.tenantDbDetails !== null,
          });
        }
      }
      setCompanyNames(uniqueCompanies);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getTenentList();
  }, [getTenentList]);

  const generateOrderId = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await generateTempOrderId();
      setOrderId(response?.data?.data);
      setAlert({
        isOpen: true,
        message: `New Order ID is generated successfully.`,
        type: "success",
      });
      setIsOpen(true);
    } catch (error) {
      console.error("Error generating order ID:", error);
      setAlert({
        isOpen: true,
        message: "Failed to generate order ID. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAddClick = async () => {
    setCrumbMappingOption([
      {
        label: "Order Management",
        link: "#",
      },
      {
        label: "Orders",
        link: routes.ORDERSLIST,
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(ordersBreadcrumbLink);
          setIsViewOpen(false);
          setIsOpen(false);
          setCurrentPage(1);
        },
      },
      { label: "Create Order", link: "" },
    ]);
    generateOrderId();
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const formattedData = useMemo(() => {
    return data?.map((item: any) => {
      const {
        id,
        order_number,
        company_name,
        customer_name,
        ordered_date,
        order_status,
        payment_status,
        plan_name,
        invoice_count,
        plan_type,
      } = item;

      const formattedDate = ordered_date ? formatDate(ordered_date) : "-";
      const totalAmount = item.total_amount
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
          }).format(item.total_amount)
        : "-";

      return {
        id: id,
        orderId: order_number || "-",
        companyName: company_name || "-",
        customer: customer_name || "-",
        orderDate: formattedDate,
        orderStatus: capitalize(order_status) || "-",
        paymentStatus: capitalize(payment_status) || "-",
        totalAmount: totalAmount || "-",
        planName: plan_name || "-",
        invoiceCount: invoice_count || "-",
        planType: capitalize(plan_type) || "-",
      };
    });
  }, [data]);

  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading Orders...";
    }
    if (searchText) {
      return "No orders found.";
    }
    return "No orders available.";
  }, [isLoading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateFilterActive = filters.orderFromDate || filters.orderToDate;

    const otherFilters = { ...filters };
    delete otherFilters.orderFromDate;
    delete otherFilters.orderToDate;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (dateFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [filters]);

  return (
    <>
      <div className="order-list-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={crumbleMappingOption} />
        </div>
        {isOpen ? (
          <CreateOrder
            setIsOpen={setIsOpen}
            orderId={orderId}
            companyNames={companyNames}
          />
        ) : (
          <>
            <div className="order-list-filter-wrapper">
              {isMobile ? (
                <div className="search-filter-group">
                  <div className="search-input-wrapper">
                    <SearchInputComponent
                      placeholder="Search by company name, customer etc.,"
                      onSearch={handleSearch}
                    />
                  </div>
                  <div className="order-list-icon-filter">
                    <Badge
                      badgeContent={appliedFilterCount}
                      invisible={appliedFilterCount === 0}
                      color="secondary"
                      overlap="circular"
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      sx={{
                        "& .MuiBadge-badge": {
                          top: "1px",
                          right: "14px",
                          transform: "scale(1)",
                        },
                      }}
                    >
                      <div
                        className={`filter-icon ${isFilterOpen ? "active-filter" : ""}`}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        style={{ cursor: "pointer" }}
                      >
                        <img src={icons.FilterIcon} alt="filter-icon" />
                      </div>
                    </Badge>
                  </div>
                </div>
              ) : (
                <div className="search-input-wrapper">
                  <SearchInputComponent
                    placeholder="Search by company name, customer etc.,"
                    onSearch={handleSearch}
                  />
                </div>
              )}
              <div className="filter-right-section d-flex">
                <CompanyFilter
                  options={companyNames}
                  filters={filters}
                  onApplyFilter={(filterData: any) => {
                    setCompanyFilter(filterData);
                  }}
                />
                {!isMobile && (
                  <Badge
                    badgeContent={appliedFilterCount}
                    invisible={appliedFilterCount === 0}
                    color="secondary"
                    overlap="circular"
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    sx={{
                      "& .MuiBadge-badge": {
                        top: "1px",
                        right: "14px",
                        transform: "scale(1)",
                      },
                    }}
                  >
                    <div
                      className={`filter-icon ${isFilterOpen ? "active-filter" : ""}`}
                      onClick={() => setIsFilterOpen(!isFilterOpen)}
                      style={{ cursor: "pointer" }}
                    >
                      <img src={icons.FilterIcon} alt="filter-icon" />
                    </div>
                  </Badge>
                )}
                {canWrite ? (
                  <Button label="Create Order" onClick={handleAddClick} />
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div className="order-list-table-filter">
              <div className="order-list-table">
                <MUITable
                  headers={headers}
                  data={formattedData}
                  onSort={handleSort}
                  noResultsTxt={noResultsTxt}
                  onRowClick={(rowData: any) =>
                    navigate(routes.ORDER_DETAILS, {
                      state: {
                        ordersDetails: rowData,
                      },
                    })
                  }
                />
              </div>
              {isFilterOpen ? (
                <OrderFilters
                  open={isFilterOpen}
                  filters={filters}
                  onClose={() => setIsFilterOpen(false)}
                  companyNames={companyNames}
                  onApplyFilters={(filterData, shouldClose = true) => {
                    try {
                      setFilters(filterData);
                      if (shouldClose) setIsFilterOpen(false);
                      setCurrentPage(1);
                    } catch (error: any) {
                      console.error("Failed to apply filters:", error);
                      setAlert({
                        isOpen: true,
                        message: "Failed to apply filters. Please try again.",
                        type: "error",
                      });
                    }
                  }}
                />
              ) : (
                <></>
              )}
            </div>
            {totalRecords && totalRecords > 0 ? (
              <div className="orders-pagination-container" >
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </>
        )}

        {alert?.isOpen && (
          <AlertComponent
            message={alert?.message || ""}
            type={
              ["info", "success", "error", "warning"].includes(
                alert?.type || ""
              )
                ? (alert?.type as "info" | "success" | "error" | "warning")
                : "info"
            }
            open={alert?.isOpen}
            onClose={() => setAlert({})}
          />
        )}
      </div>
    </>
  );
};
export default Orders;
